import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SeamId } from './theme';

/**
 * `useLayoutEffect` in the browser, `useEffect` on the server — where neither runs, and the layout
 * one would only log a warning on every render of every route that draws the shell.
 */
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * The seam's own width, and the only place the number lives. It is a real element between the
 * panes (D1), so it comes out of the row's arithmetic exactly once per seam and the drag has to
 * account for it — 380 + 1 + 252 + 1 + 878 = 1512.
 */
export const SEAM_PX = 1;

export interface PaneLimits {
  agentMin: number;
  agentMax: number;
  directoryMin: number;
  directoryMax: number;
  /** The Properties pane docked to the preview's right. Only in the arithmetic while it is open. */
  inspectorMin: number;
  inspectorMax: number;
  /** The preview has no handle of its own, so its floor is enforced from the other seams. */
  previewMin: number;
}

/**
 * **NOT IN DESIGN — defaults chosen in code.**
 *
 * Nothing in Paper constrains the drag: LB9 authors the seam's colour and nothing else, and no pane
 * carries a `min-width` or `max-width` on any of the nine boards. These are a starting position,
 * exposed as a prop so they can be corrected from a Storybook control rather than a commit. Do not
 * quote them back as measured values.
 */
export const DEFAULT_PANE_LIMITS: PaneLimits = {
  // 360 and not 300 (Beka, 2026-09-05). `ConversationColumn` is drawn against a 400px stage and the
  // pane pays 12px of inset on each side, so a 300px pane renders the thread at 276 — 31% under the
  // measure every one of its parts was drawn to, and `UserBubble`'s 320px cap stops fitting at all.
  agentMin: 360,
  agentMax: 560,
  directoryMin: 200,
  directoryMax: 420,
  // The Properties pane's pair arrived with the pane (2026-09-24) and carries the same caveat: no
  // board draws the pane, so there is nothing to measure. The ceiling is the directory's own.
  inspectorMin: 248,
  inspectorMax: 420,
  previewMin: 480,
};

/** The design's resting widths — these two ARE measured (LB1's caption). */
export const DEFAULT_AGENT_WIDTH = 380;
export const DEFAULT_DIRECTORY_WIDTH = 252;
/**
 * The Properties pane's resting width. NOT measured, unlike the two above — it was chosen in code
 * alongside its limits, for the same reason they were.
 */
export const DEFAULT_INSPECTOR_WIDTH = 280;

/** The fixed panes' live widths, in px: what each takes out of its row while it is open. */
export interface PaneWidths {
  agent: number;
  directory: number;
  inspector: number;
}

/**
 * Which of the panes that share the split row with the preview are mounted. The agent has no flag
 * here because nothing in `paneSpare` depends on it: the agent is the only pane in the BODY's row,
 * and whether it is open decides how wide the split row is, not what the split row has to seat.
 */
export interface PanesOpen {
  directory: boolean;
  inspector: boolean;
}

/**
 * The most pane `id` can take out of `track` before the preview drops under its floor: the room the
 * OTHER panes leave it, before its own configured range has a say.
 *
 * `track` is the row the pane's seam divides — the body for the agent, the split row for the
 * directory and the inspector. The pane's own seam comes out once, and every OPEN neighbour costs
 * its width plus its seam:
 *
 * - agent: the browser column still has to seat the directory, the inspector, their seams, and the
 *   preview's floor.
 * - directory: the split row still has to seat the inspector, its seam, and the floor.
 * - inspector: the directory's mirror — the directory, its seam, and the floor.
 *
 * Pure, so the arithmetic can be pinned without a DOM. With the inspector closed every term it adds
 * is zero, and the agent's and the directory's numbers are exactly the ones this hook produced before
 * the inspector existed; `__tests__/paneMath.test.ts` holds it to that.
 */
export function paneSpare(
  id: SeamId,
  track: number,
  widths: PaneWidths,
  open: PanesOpen,
  limits: PaneLimits,
): number {
  const directory = open.directory ? widths.directory + SEAM_PX : 0;
  const inspector = open.inspector ? widths.inspector + SEAM_PX : 0;
  if (id === 'agent') return track - SEAM_PX - directory - inspector - limits.previewMin;
  if (id === 'directory') return track - SEAM_PX - inspector - limits.previewMin;
  return track - SEAM_PX - limits.previewMin - directory;
}

/** A pane's configured range: the drag's hard stops, before the preview's floor has a say. */
function paneRange(id: SeamId, limits: PaneLimits): [min: number, max: number] {
  if (id === 'agent') return [limits.agentMin, limits.agentMax];
  if (id === 'directory') return [limits.directoryMin, limits.directoryMax];
  return [limits.inspectorMin, limits.inspectorMax];
}

/**
 * The width a proposal for `id` actually lands at: its configured range, capped again by what the
 * preview can spare, and rounded. The whole of the hook's `apply` arithmetic without its side effects
 * — pure for the same reason `paneSpare` is.
 */
export function clampPane(
  id: SeamId,
  next: number,
  track: number,
  widths: PaneWidths,
  open: PanesOpen,
  limits: PaneLimits,
): number {
  const [min, max] = paneRange(id, limits);
  return Math.round(clamp(next, min, Math.min(max, paneSpare(id, track, widths, open, limits))));
}

/** Everything a `Seam` needs to render itself and to start a drag. */
export interface SeamControl {
  /** Live width of the pane this seam sizes. */
  value: number;
  min: number;
  max: number;
  dragging: boolean;
  onDragStart: (event: React.PointerEvent<HTMLElement>) => void;
  /** Signed pixels, from the keyboard. */
  onNudge: (delta: number) => void;
}

/**
 * A width change. `fromUser` is true for a drag or an arrow key, and false for the window's own clamp: a
 * host that remembers widths saves only the first kind, or a narrow window would ratchet what it saved.
 */
export type PaneWidthChange = (width: number, fromUser: boolean) => void;

export interface UseResizablePanesOptions {
  /** Controlled agent width. Leave undefined and the hook holds it, so a story drags standalone. */
  agentWidth?: number;
  defaultAgentWidth?: number;
  onAgentWidthChange?: PaneWidthChange;
  directoryWidth?: number;
  defaultDirectoryWidth?: number;
  onDirectoryWidthChange?: PaneWidthChange;
  inspectorWidth?: number;
  defaultInspectorWidth?: number;
  onInspectorWidthChange?: PaneWidthChange;
  /**
   * Widths remembered from an earlier visit, taken as what the user ASKED for once they arrive, then
   * clamped like any other wanted width. They arrive after mount (storage is the browser's, and the
   * server's render must match the first client one), so the defaults above have already been used; a
   * new object is applied again, the same object never twice.
   */
  savedWidths?: Partial<PaneWidths> | null;
  /** Anything omitted falls back to `DEFAULT_PANE_LIMITS`. */
  limits?: Partial<PaneLimits>;
  /** The flex row the agent seam divides: agent | seam | browser column. */
  bodyRef: React.RefObject<HTMLElement | null>;
  /**
   * The flex row the directory and inspector seams divide: directory | seam | preview | seam |
   * inspector. One track for both, because both panes are paid for out of the same preview.
   */
  splitRef: React.RefObject<HTMLElement | null>;
  /**
   * A folded directory is unmounted along with its seam (D4), so neither is part of the arithmetic
   * and all of the freed width belongs to the preview.
   */
  directoryOpen?: boolean;
  /**
   * Which edge of the split row the directory sits on. On the RIGHT its seam is on its left, so a
   * rightward drag SHRINKS the pane — the sign of every pointer and arrow-key delta flips, and
   * nothing else does: the clamp arithmetic deals only in widths.
   */
  directorySide?: 'left' | 'right';
  /**
   * Same for the agent. The hook needs both flags rather than just the directory's: a pane that
   * folds mid-drag takes its seam — and the only element the drag was hanging off — out of the
   * tree, and the fold is also what decides whether the agent's width is still in the row's
   * arithmetic when the window re-clamps everything.
   */
  agentOpen?: boolean;
  /**
   * The Properties pane, docked to the preview's right. CLOSED by default, unlike the other two —
   * it is the one pane a shell has to ask for, and a shell that never does keeps every number it had
   * before the inspector existed. While it is open, its width and seam come out of the agent's and
   * the directory's spare room as well as its own. Its seam sits on its LEFT edge, so like a
   * right-docked directory it drags backwards.
   */
  inspectorOpen?: boolean;
}

export interface ResizablePanes {
  agentWidth: number;
  directoryWidth: number;
  inspectorWidth: number;
  /** Which seam owns the pointer, or `null`. The shell reads it to suppress text selection. */
  dragging: SeamId | null;
  agentSeam: SeamControl;
  directorySeam: SeamControl;
  inspectorSeam: SeamControl;
}

/**
 * Keeps `value` inside the range, and refuses to let a squeezed range invert.
 *
 * On a window too narrow for everyone's minimum the pane's own floor wins and the preview gives:
 * the alternative is a `max` below `min`, which makes the seam jump backwards under the pointer.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * The pane seams, dragged — the agent's and the directory's, and the inspector's while it is open.
 *
 * Pointer-events with `setPointerCapture`, because the pointer leaves an 8px band on the first
 * frame of any real drag and everything after that has to keep arriving at the seam rather than at
 * whatever is under the cursor.
 *
 * Widths come back as numbers and the shell writes them to custom properties. Nothing here
 * animates: a transitioned width lags the pointer by the length of the transition, and a resize
 * that arrives where the cursor was instead of where it is stops feeling like direct manipulation.
 */
export function useResizablePanes({
  agentWidth,
  defaultAgentWidth = DEFAULT_AGENT_WIDTH,
  onAgentWidthChange,
  directoryWidth,
  defaultDirectoryWidth = DEFAULT_DIRECTORY_WIDTH,
  onDirectoryWidthChange,
  inspectorWidth,
  defaultInspectorWidth = DEFAULT_INSPECTOR_WIDTH,
  onInspectorWidthChange,
  limits,
  bodyRef,
  splitRef,
  directoryOpen = true,
  directorySide = 'left',
  agentOpen = true,
  inspectorOpen = false,
  savedWidths = null,
}: UseResizablePanesOptions): ResizablePanes {
  const [internalAgent, setInternalAgent] = useState(defaultAgentWidth);
  const [internalDirectory, setInternalDirectory] = useState(defaultDirectoryWidth);
  const [internalInspector, setInternalInspector] = useState(defaultInspectorWidth);
  const [dragging, setDragging] = useState<SeamId | null>(null);

  const agent = agentWidth !== undefined ? agentWidth : internalAgent;
  const directory = directoryWidth !== undefined ? directoryWidth : internalDirectory;
  const inspector = inspectorWidth !== undefined ? inspectorWidth : internalInspector;

  /**
   * The native pointermove listener is registered once per drag and closes over whatever the widths
   * were at that moment. These mirrors let it read the current ones without the drag having to be
   * torn down and rebuilt on every frame.
   */
  const agentRef = useRef(agent);
  agentRef.current = agent;
  const directoryRef = useRef(directory);
  directoryRef.current = directory;
  const inspectorRef = useRef(inspector);
  inspectorRef.current = inspector;

  /**
   * What the user last ASKED for, before the preview's floor had its say.
   *
   * The rendered width and the wanted width are not the same number, and conflating them makes the
   * clamp destructive: a window narrow enough to squeeze the panes would overwrite the wanted width
   * with the squeezed one, and widening the window again could never recover it because there would
   * be nothing left saying what it used to be. Every resize would ratchet the layout one step
   * smaller until both panes sat on their minimums for good. So the observer clamps THIS, and the
   * only things that write it are the drag, the arrow keys and a controlled prop.
   */
  const wantedAgentRef = useRef(agent);
  const wantedDirectoryRef = useRef(directory);
  const wantedInspectorRef = useRef(inspector);
  const directoryOpenRef = useRef(directoryOpen);
  directoryOpenRef.current = directoryOpen;
  const agentOpenRef = useRef(agentOpen);
  agentOpenRef.current = agentOpen;
  const inspectorOpenRef = useRef(inspectorOpen);
  inspectorOpenRef.current = inspectorOpen;
  const directorySideRef = useRef(directorySide);
  directorySideRef.current = directorySide;

  /**
   * +1 for a seam whose pane grows rightward, -1 for one whose pane is docked to the right of it:
   * the inspector always, and the directory when it is docked there.
   */
  const deltaSign = (id: SeamId): number =>
    id === 'inspector' || (id === 'directory' && directorySideRef.current === 'right') ? -1 : 1;

  /** The live width of the pane a seam sizes, read from its mirror. */
  const widthOf = (id: SeamId): number =>
    id === 'agent' ? agentRef.current : id === 'directory' ? directoryRef.current : inspectorRef.current;

  const bounds: PaneLimits = { ...DEFAULT_PANE_LIMITS, ...limits };
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;

  const setAgent = useCallback(
    (width: number, fromUser: boolean) => {
      if (agentWidth === undefined) setInternalAgent(width);
      onAgentWidthChange?.(width, fromUser);
    },
    [agentWidth, onAgentWidthChange],
  );

  const setDirectory = useCallback(
    (width: number, fromUser: boolean) => {
      if (directoryWidth === undefined) setInternalDirectory(width);
      onDirectoryWidthChange?.(width, fromUser);
    },
    [directoryWidth, onDirectoryWidthChange],
  );

  const setInspector = useCallback(
    (width: number, fromUser: boolean) => {
      if (inspectorWidth === undefined) setInternalInspector(width);
      onInspectorWidthChange?.(width, fromUser);
    },
    [inspectorWidth, onInspectorWidthChange],
  );

  /**
   * Applies a proposed width, clamped by the configured range AND by what the preview can spare.
   *
   * `trackWidth` is passed in rather than measured here: during a drag it is read once, at the
   * start, because the window cannot resize mid-drag and a layout read on every pointermove is the
   * classic way to make a resize lag the pointer it is meant to follow.
   */
  const apply = useCallback(
    (id: SeamId, next: number, trackWidth: number, fromUser = true) => {
      const lim = boundsRef.current;
      // The neighbours as they are NOW, from the mirrors: a held arrow key and a drag both arrive
      // faster than React re-renders, so the render's widths would already be a step behind.
      const widths: PaneWidths = {
        agent: agentRef.current,
        directory: directoryRef.current,
        inspector: inspectorRef.current,
      };
      const open: PanesOpen = {
        directory: directoryOpenRef.current,
        inspector: inspectorOpenRef.current,
      };
      if (id === 'agent') {
        // Recorded before the preview's floor is applied, and only for a width the user actually
        // asked for — the observer's own proposals must not become the new intent.
        if (fromUser) wantedAgentRef.current = clamp(next, lim.agentMin, lim.agentMax);
        // Whatever the agent takes, the browser column still has to seat its directory, that
        // directory's own seam, an open inspector and ITS seam, and the preview's floor.
        const width = clampPane('agent', next, trackWidth, widths, open, lim);
        // A drag held against a limit, and every frame of a window resize, propose a width the pane
        // already has. Dropping those here keeps a controlled caller's `onAgentWidthChange` from
        // firing dozens of times with a number that never changed.
        if (width === agentRef.current) return;
        // Write the mirror BEFORE the state, so a second nudge in the same tick starts from the
        // width this one just chose. Held arrow keys arrive faster than React re-renders, and
        // reading `agent` there would make every repeat compute the same single-pixel step.
        agentRef.current = width;
        setAgent(width, fromUser);
        return;
      }
      if (id === 'directory') {
        if (fromUser) wantedDirectoryRef.current = clamp(next, lim.directoryMin, lim.directoryMax);
        // The split row seats the preview's floor and, while it is open, the inspector beside it.
        const width = clampPane('directory', next, trackWidth, widths, open, lim);
        if (width === directoryRef.current) return;
        directoryRef.current = width;
        setDirectory(width, fromUser);
        return;
      }
      if (fromUser) wantedInspectorRef.current = clamp(next, lim.inspectorMin, lim.inspectorMax);
      // The directory's mirror: the same row, the same floor, and the directory as the neighbour.
      const width = clampPane('inspector', next, trackWidth, widths, open, lim);
      if (width === inspectorRef.current) return;
      inspectorRef.current = width;
      setInspector(width, fromUser);
    },
    [setAgent, setDirectory, setInspector],
  );

  /** Ends whatever drag is live. Held in a ref so unmounting mid-drag can call it too. */
  const endDrag = useRef<(() => void) | null>(null);
  useEffect(() => () => endDrag.current?.(), []);

  /**
   * A pane can fold while its own seam is being dragged — the app header's agent control and its
   * cmd-Alt-A both do it, and so does any state the consuming app syncs in. Collapse is an unmount
   * (D4), so the seam under the pointer leaves the tree mid-drag and there is nothing left to
   * deliver the pointerup to. `lostpointercapture` covers the browser's own release, and this
   * covers everything that folds a pane without the pointer ever moving again: without one of them
   * `dragging` never returns to null and the whole shell keeps `cursor: col-resize` and
   * `user-select: none` for the rest of the session.
   */
  useEffect(() => {
    if (dragging === 'agent' && !agentOpen) endDrag.current?.();
    if (dragging === 'directory' && !directoryOpen) endDrag.current?.();
    if (dragging === 'inspector' && !inspectorOpen) endDrag.current?.();
  }, [dragging, agentOpen, directoryOpen, inspectorOpen]);

  /**
   * Re-proposes every pane's WANTED width against the body as it measures now. The observer below
   * runs it on every size change, and the inspector's fold runs it too — see the effect after that.
   */
  const reclamp = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const track = body.getBoundingClientRect().width;
    // A shell inside a hidden tab measures 0, and clamping against that pins both panes to their
    // minimums for a fold the user never asked for.
    if (track === 0) return;
    // The split row is DERIVED rather than measured: a clamp in this same pass has not been through
    // layout yet, so the row's own box is still a frame behind and would be clamped against a width
    // that no longer exists. A function, because the agent's clamp below changes it.
    const split = () => track - (agentOpenRef.current ? agentRef.current + SEAM_PX : 0);
    // The inspector goes FIRST, and only while it is open. It is the pane that gives first when the
    // window is short — the rightmost, the one opened last, and the only one the user asked for on
    // top of a layout that already fit — so the agent and the directory only move once it is down
    // to its own minimum. Closed, it is skipped outright and the pass below is the one that ran
    // before it existed.
    if (inspectorOpenRef.current) apply('inspector', wantedInspectorRef.current, split(), false);
    // WANTED, not current: re-proposing the current width can only ever ratchet down, because a
    // width that has already been squeezed clamps to itself no matter how much room comes back.
    apply('agent', wantedAgentRef.current, track, false);
    apply('directory', wantedDirectoryRef.current, split(), false);
  }, [apply, bodyRef]);

  /**
   * THE REMEMBERED LAYOUT. Written straight into the wanted widths, clamped to each pane's own range, and
   * then re-proposed through the same pass a window resize makes: that pass is what fits them to the
   * window the user has now, which may be narrower than the one they were saved in.
   */
  useIsoLayoutEffect(() => {
    if (!savedWidths) return;
    const lim = boundsRef.current;
    if (savedWidths.agent !== undefined) wantedAgentRef.current = clamp(savedWidths.agent, lim.agentMin, lim.agentMax);
    if (savedWidths.directory !== undefined) {
      wantedDirectoryRef.current = clamp(savedWidths.directory, lim.directoryMin, lim.directoryMax);
    }
    if (savedWidths.inspector !== undefined) {
      wantedInspectorRef.current = clamp(savedWidths.inspector, lim.inspectorMin, lim.inspectorMax);
    }
    reclamp();
  }, [savedWidths, reclamp]);

  /**
   * The window can take width away from panes that only ever gave it up to a drag.
   *
   * `apply` protects the preview's floor, but until this it only ran when the user touched a seam:
   * shrink the window instead and the two fixed columns keep their widths, the preview is the only
   * thing that flexes, and it goes to zero — taking the directory's seam off-screen with it, which
   * is the one handle that could have given the width back. Re-running the same clamp on every size
   * change makes the window obey the floor the drag already did.
   */
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => reclamp());
    observer.observe(body);
    return () => observer.disconnect();
  }, [reclamp, bodyRef]);

  /**
   * Opening the inspector takes width out of the split row without the window changing size, so the
   * observer never hears about it: the preview would sit under its floor until the window next moved.
   * Closing it is the same problem the other way round — whatever the opening squeezed would stay
   * squeezed with the room to recover sitting unused. So the fold itself re-runs the clamp.
   *
   * Keyed on the inspector ALONE, and on a CHANGE rather than on mount: the observer already clamps
   * on mount, and the agent and directory folds have never re-clamped, so a shell that never opens
   * the inspector runs exactly the passes it ran before there was one. A layout effect, so the clamp
   * lands before the first frame that shows the pane rather than one frame after it.
   */
  const inspectorWasOpen = useRef(inspectorOpen);
  useIsoLayoutEffect(() => {
    if (inspectorWasOpen.current === inspectorOpen) return;
    inspectorWasOpen.current = inspectorOpen;
    reclamp();
  }, [inspectorOpen, reclamp]);

  const beginDrag = useCallback(
    (id: SeamId, event: React.PointerEvent<HTMLElement>) => {
      // Secondary buttons open context menus; a resize that starts on one would swallow it.
      if (event.button !== 0) return;
      const node = event.currentTarget;
      // The directory and the inspector both divide the split row, so both are measured off it.
      const track = id === 'agent' ? bodyRef.current : splitRef.current;
      if (!track) return;

      // Stops the browser starting a text selection or a native drag on the pane text either side
      // of the seam. Once one of those begins it owns every subsequent pointermove, not us.
      event.preventDefault();
      // The pointer is off the 8px band within a frame or two of any real drag; capture keeps the
      // events coming here instead of to whatever it crosses.
      node.setPointerCapture(event.pointerId);

      const startX = event.clientX;
      const startWidth = widthOf(id);
      const trackWidth = track.getBoundingClientRect().width;

      const onMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== event.pointerId) return;
        // A move with nothing pressed is proof the button came up somewhere we never heard about —
        // released over native browser chrome, over another application, or after a cmd-Tab away.
        // Without this the seam chases a bare cursor and the shell stays in its resizing state
        // until the user presses and releases on the seam again.
        if (moveEvent.buttons === 0) {
          finish();
          return;
        }
        apply(id, startWidth + deltaSign(id) * (moveEvent.clientX - startX), trackWidth);
      };

      // Hoisted on purpose: `onMove` has to be able to end the drag, and `finish` has to be able to
      // unregister `onMove`.
      function finish() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', finish);
        window.removeEventListener('pointercancel', finish);
        node.removeEventListener('lostpointercapture', finish);
        // A pointercancel — the OS taking the pointer, a touch the browser decides is a scroll —
        // has to end the drag exactly like a pointerup. Without it the seam stays lit and the next
        // press re-enters a drag that never stopped.
        if (node.isConnected && node.hasPointerCapture(event.pointerId)) {
          node.releasePointerCapture(event.pointerId);
        }
        endDrag.current = null;
        setDragging(null);
      }

      // On the WINDOW, not on the seam. Capture already retargets every move to the seam, so
      // nothing is lost by listening further out — but the pane can fold mid-drag (D4 unmounts it),
      // and a listener that left the document with its element never hears the pointerup. The drag
      // would then never end, and the shell would keep `cursor: col-resize` and `user-select: none`
      // over every control in the app until it was reloaded.
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', finish);
      window.addEventListener('pointercancel', finish);
      // Fires when the browser drops the capture implicitly — which is what removing the seam from
      // the document does — so the drag ends on the spot rather than waiting for a pointerup that
      // may be several seconds away.
      node.addEventListener('lostpointercapture', finish);
      endDrag.current = finish;
      setDragging(id);
    },
    [apply, bodyRef, splitRef],
  );

  const nudge = useCallback(
    (id: SeamId, delta: number) => {
      const track = id === 'agent' ? bodyRef.current : splitRef.current;
      if (!track) return;
      // A keypress is not a per-frame event, so measuring here is free — and it has to be measured,
      // because the window may well have changed size since the last drag.
      apply(id, widthOf(id) + deltaSign(id) * delta, track.getBoundingClientRect().width);
    },
    [apply, bodyRef, splitRef],
  );

  return {
    agentWidth: agent,
    directoryWidth: directory,
    inspectorWidth: inspector,
    dragging,
    agentSeam: {
      value: agent,
      // The CONFIGURED range, not the space-aware one. `apply` additionally protects the preview's
      // floor, but that floor moves with the window, and a reported range that changed every time
      // the user resized the browser would be read out differently on every visit to the seam.
      min: bounds.agentMin,
      max: bounds.agentMax,
      dragging: dragging === 'agent',
      onDragStart: (event) => beginDrag('agent', event),
      onNudge: (delta) => nudge('agent', delta),
    },
    directorySeam: {
      value: directory,
      min: bounds.directoryMin,
      max: bounds.directoryMax,
      dragging: dragging === 'directory',
      onDragStart: (event) => beginDrag('directory', event),
      onNudge: (delta) => nudge('directory', delta),
    },
    inspectorSeam: {
      value: inspector,
      min: bounds.inspectorMin,
      max: bounds.inspectorMax,
      dragging: dragging === 'inspector',
      onDragStart: (event) => beginDrag('inspector', event),
      onNudge: (delta) => nudge('inspector', delta),
    },
  };
}
